import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useV2Auth } from '@/context/V2AuthContext';
import '@/components/mask/mask.css';

/* Phase 4 — ICH: persönlicher Raum in drei Ebenen. Kein Dashboard, keine Cards. */

const SignetGedanken = () => (
  <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true" className="shrink-0 text-[var(--text)]/70">
    <circle cx="13" cy="13" r="4" fill="currentColor" />
  </svg>
);

const SignetBewahren = () => (
  <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true" className="shrink-0 text-[var(--text)]/70">
    <circle cx="13" cy="13" r="10" fill="none" stroke="currentColor" strokeWidth="1.1" />
    <circle cx="13" cy="13" r="3.2" fill="currentColor" />
  </svg>
);

export default function IchV2() {
  const { user } = useV2Auth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    document.body.classList.add('cc-no-overlays');
    return () => document.body.classList.remove('cc-no-overlays');
  }, []);

  if (!user) return null;
  const firstName = (user.name || '').split(' ')[0];

  /* Beta-Relaunch: Mein Innerstes, Lebensliste & Herzensmenschen geparkt (Daten bleiben erhalten) */
  const MORE_LINKS = [
    { label: t('v2.ich.publicProfile'), to: '/profile-classic#public-profile-section', testid: 'ich-public-link' },
  ];

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-x-hidden" data-testid="ich-room">
      <div className="mask-bg-word mask-bg-top" aria-hidden="true">
        <span style={{ transform: 'translateX(-6%)' }}>{t('v2.bgWords.ich')}</span>
      </div>

      <div className="relative z-10 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-20 pb-28">
        <header className="mb-24">
          {firstName && <p className="mask-enter text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-5">{firstName}</p>}
          <h1 className="mask-enter mask-enter-delay-1 mask-headline text-3xl sm:text-4xl text-[var(--text)]" data-testid="ich-headline">
            {t('v2.ich.headline')}
          </h1>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] mt-5 max-w-md">
            {t('v2.ich.sub')}
          </p>
        </header>

        <section className="mb-28 space-y-20 mask-enter mask-enter-delay-2">
          <button
            type="button"
            onClick={() => navigate('/tagebuch')}
            data-testid="ich-tagebuch-link"
            className="mask-room-item group block w-full text-left"
          >
            <div className="flex items-start gap-5">
              <span className="mt-1"><SignetGedanken /></span>
              <span className="block">
                <span className="block text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-3">{t('v2.ich.gedankenLabel')}</span>
                <span className="block mask-headline text-2xl sm:text-3xl text-[var(--text)] mb-2.5">
                  {t('v2.ich.gedankenTitle')}
                </span>
                <span className="block text-sm text-[var(--muted)]">
                  {t('v2.ich.gedankenSub')}
                  <span aria-hidden="true" className="inline-block ml-2 transition-transform group-hover:translate-x-1">→</span>
                </span>
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => navigate('/bewahrt')}
            data-testid="ich-bewahrt-link"
            className="mask-room-item group block w-full text-left"
          >
            <div className="flex items-start gap-5">
              <span className="mt-1"><SignetBewahren /></span>
              <span className="block">
                <span className="block text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-3">{t('v2.ich.bewahrenLabel')}</span>
                <span className="block mask-headline text-2xl sm:text-3xl text-[var(--text)] mb-2.5">
                  {t('v2.ich.bewahrenTitle')}
                </span>
                <span className="block text-sm text-[var(--muted)]">
                  {t('v2.ich.bewahrenSub')}
                  <span aria-hidden="true" className="inline-block ml-2 transition-transform group-hover:translate-x-1">→</span>
                </span>
              </span>
            </div>
          </button>
        </section>

        <section className="mb-20 mask-enter mask-enter-delay-3" data-testid="ich-more-section">
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-4">{t('v2.ich.more')}</p>
          <div className="divide-y divide-[var(--border)]/30">
            {MORE_LINKS.map((link) => (
              <button
                key={link.testid}
                type="button"
                onClick={() => navigate(link.to)}
                data-testid={link.testid}
                className="group w-full flex items-center justify-between py-4.5 sm:py-5 text-[15px] text-[var(--text)]/60 hover:text-[var(--text)] transition-colors"
                style={{ paddingTop: '1.1rem', paddingBottom: '1.1rem' }}
              >
                <span>{link.label}</span>
                <span aria-hidden="true" className="text-[var(--muted)] transition-transform group-hover:translate-x-1">→</span>
              </button>
            ))}
          </div>
        </section>

        <footer className="pt-6">
          <button
            type="button"
            onClick={() => navigate('/profile-classic')}
            data-testid="ich-settings-link"
            className="text-sm text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors"
          >
            {t('v2.ich.settings')}
          </button>
        </footer>
      </div>
    </div>
  );
}
